import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from '../services/notification.service';
import { Notification } from '../models/notification.model';

@Component({
  selector: 'app-top-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './top-banner.component.html',
  styleUrls: ['./top-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopBannerComponent implements OnInit, OnDestroy {
  @Input() key: string | undefined;
  @Input() top = 0;
  @Output() onNotificationClose = new EventEmitter<Notification>();

  notifications: Notification[] = [];

  private readonly destroy$ = new Subject<void>();
  private readonly timeoutByIndex = new Map<number, ReturnType<typeof setTimeout>>();

  constructor(
    private readonly notificationService: NotificationService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.notificationService
      .getNotifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe((notifications) => {
        this.timeoutByIndex.forEach((timeoutId) => clearTimeout(timeoutId));
        this.timeoutByIndex.clear();
        this.notifications = notifications.filter((notification) => this.canAdd(notification));
        this.applyTimeouts();
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.timeoutByIndex.forEach((timeoutId) => clearTimeout(timeoutId));
    this.timeoutByIndex.clear();
    this.destroy$.next();
    this.destroy$.complete();
  }

  hideBar(index: number): void {
    this.clearTimeout(index);
    const notification = this.notifications[index];
    if (notification) {
      this.notificationService.remove(notification);
    }
    if (notification) {
      this.onNotificationClose.emit(notification);
    }
  }

  severityClass(severity: string | undefined): string {
    const severityValue = (severity || '').toLowerCase();
    if (severityValue === 'error') {
      return 'fmc-top-banner--error';
    }

    if (severityValue === 'warning') {
      return 'fmc-top-banner--warning';
    }

    if (severityValue === 'success') {
      return 'fmc-top-banner--success';
    }

    return 'fmc-top-banner--info';
  }

  private canAdd(notification: Notification): boolean {
    if (!this.key) {
      return true;
    }

    return notification.key === this.key;
  }

  private applyTimeouts(): void {
    this.notifications.forEach((notification, index) => {
      const timeout = notification.timeout ?? 5000;
      if (timeout <= 0 || this.timeoutByIndex.has(index)) {
        return;
      }

      const timeoutId = setTimeout(() => {
        this.hideBar(index);
      }, timeout);

      this.timeoutByIndex.set(index, timeoutId);
    });
  }

  private clearTimeout(index: number): void {
    const timeoutId = this.timeoutByIndex.get(index);
    if (!timeoutId) {
      return;
    }

    clearTimeout(timeoutId);
    this.timeoutByIndex.delete(index);
  }
}
